import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource, ValidTitleTags } from '../../types'
import { Badge } from '../Badge'
import { Picture } from '../Picture'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './CategoryHero.module.scss'

export const CategoryHero: FunctionComponent<CategoryHeroProps> = ({
  className,
  description = '',
  heading = '',
  image,
  labTested = false,
  headingTag = 'h2',
  ...rest
}) => (
  <section
    className={classnames(
      className,
      styles.categoryHero,
      image?.src && styles.hasImage
    )}
    {...rest}
  >
    <div className={styles.content}>
      <div className={styles.text}>
        {labTested && (
          <Badge
            theme="lab tested"
            className={classnames('sb-margin-bottom-2', styles.badge)}
          />
        )}
        {heading && (
          <TypographyV2
            className={styles.heading}
            textStyle="sb-text-heading-page-title"
            tag={headingTag}
          >
            {heading}
          </TypographyV2>
        )}

        {description && (
          <TypographyV2
            className={classnames('sb-margin-top-2', styles.description)}
            tag="p"
            textStyle="sb-text-body-default-regular"
          >
            {description}
          </TypographyV2>
        )}
      </div>
    </div>

    {image?.src && (
      <Picture
        className={styles.image}
        src={image.src || ''}
        sources={image.sources}
        alt={image.alt || ''}
        aspectRatioMain="two-to-one"
        aria-hidden="true"
        imageClassName={styles.heroImage}
        lazyLoad={false}
      />
    )}
  </section>
)

///////// IMPLEMENTATION /////////

export type CategoryHeroProps = {
  /**
   * Optional class name that can be applied to the CategoryHero outer wrapper for extra styling purposes
   */
  className?: string
  /**
   * Description text to be displayed above the HeroImage
   */
  description: string
  /**
   * Heading text to be displayed above the description
   */
  heading: string
  /**
   * Optional image
   */
  image?: { src: string; alt: string; sources: ImageSource[] }
  /**
   * Optional badge for lab tested products
   */
  labTested?: boolean
  /**
   * Optional prop that will set the heading size on the page
   */
  headingTag?: ValidTitleTags
} & ComponentPropsWithoutRef<'section'>
